import { useMemo, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { X } from 'lucide-react';
import { TimelineItem } from '@/types/item';

export type DependencyType = 'FS' | 'SS' | 'FF';

export type DependencyRef = {
  kind: 'item' | 'milestone';
  id: string;
  type: DependencyType;
  lag: number;
};

export interface DependencyMilestone {
  id: string;
  name: string;
  parentItemId: string;
  parentItemName: string;
}

interface DependencyPickerProps {
  availableItems: TimelineItem[];
  milestones: DependencyMilestone[];
  value: DependencyRef[];
  onChange: (next: DependencyRef[]) => void;
  excludeKind: 'item' | 'milestone';
  excludeId: string;
  scrollHeightClass?: string;
}

export default function DependencyPicker({
  availableItems,
  milestones,
  value,
  onChange,
  excludeKind,
  excludeId,
  scrollHeightClass = 'h-[280px]',
}: DependencyPickerProps) {
  const [search, setSearch] = useState('');

  const valueSet = useMemo(
    () => new Set(value.map(d => `${d.kind}:${d.id}`)),
    [value]
  );

  const toggle = (kind: 'item' | 'milestone', id: string) => {
    const exists = value.some(d => d.kind === kind && d.id === id);
    if (exists) onChange(value.filter(d => !(d.kind === kind && d.id === id)));
    else onChange([...value, { kind, id, type: 'FS', lag: 0 }]);
  };

  const updateEntry = (kind: 'item' | 'milestone', id: string, patch: Partial<DependencyRef>) => {
    onChange(value.map(d => (d.kind === kind && d.id === id ? { ...d, ...patch } : d)));
  };

  const remove = (kind: 'item' | 'milestone', id: string) => {
    onChange(value.filter(d => !(d.kind === kind && d.id === id)));
  };

  const q = search.trim().toLowerCase();

  const nameLookup = useMemo(() => {
    const m = new Map<string, string>();
    for (const it of availableItems) m.set(`item:${it.id}`, it.name);
    for (const ms of milestones) m.set(`milestone:${ms.id}`, `${ms.parentItemName} › ${ms.name}`);
    return m;
  }, [availableItems, milestones]);

  const itemGroups = useMemo(() => {
    const map = new Map<string, { id: string; name: string; sortOrder?: number }[]>();
    for (const it of availableItems) {
      if (excludeKind === 'item' && it.id === excludeId) continue;
      if (q && !it.name.toLowerCase().includes(q)) continue;
      const cat = (it.category as string) || 'Khác';
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push({ id: it.id, name: it.name, sortOrder: it.sortOrder });
    }
    for (const arr of map.values()) {
      arr.sort((a, b) => {
        const oa = a.sortOrder ?? Infinity;
        const ob = b.sortOrder ?? Infinity;
        if (oa !== ob) return oa - ob;
        return a.name.localeCompare(b.name);
      });
    }
    return Array.from(map.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([cat, items]) => ({ cat, items }));
  }, [availableItems, excludeKind, excludeId, q]);

  const milestoneGroups = useMemo(() => {
    const map = new Map<string, { id: string; name: string; parentId: string; parentName: string }[]>();
    for (const ms of milestones) {
      if (excludeKind === 'milestone' && ms.id === excludeId) continue;
      if (q && !ms.name.toLowerCase().includes(q)) continue;
      const key = ms.parentItemName || 'Khác';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push({
        id: ms.id,
        name: ms.name,
        parentId: ms.parentItemId,
        parentName: ms.parentItemName,
      });
    }
    for (const arr of map.values()) {
      arr.sort((a, b) => a.name.localeCompare(b.name));
    }
    return Array.from(map.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([parent, items]) => ({ parent, items }));
  }, [milestones, excludeKind, excludeId, q]);

  const isEmpty = itemGroups.length === 0 && milestoneGroups.length === 0;

  return (
    <div className="space-y-2">
      <Input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Tìm hạng mục / mốc..."
        className="h-9"
      />

      <div className="rounded-md border">
        <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground border-b bg-muted/30">
          Đã chọn ({value.length})
        </div>
        {value.length === 0 ? (
          <div className="px-3 py-3 text-xs text-muted-foreground">
            Chưa chọn phụ thuộc nào.
          </div>
        ) : (
          <ScrollArea className="max-h-[180px]">
            <div className="p-2 space-y-1">
              {value.map((d) => {
                const label = nameLookup.get(`${d.kind}:${d.id}`) ?? '(không tìm thấy)';
                return (
                  <div
                    key={`sel-${d.kind}-${d.id}`}
                    className="flex items-center gap-2 px-2 py-1.5 rounded-sm bg-primary/5"
                  >
                    <span className="text-sm flex-1 truncate" title={label}>{label}</span>
                    <Select
                      value={d.type}
                      onValueChange={(v) => updateEntry(d.kind, d.id, { type: v as DependencyType })}
                    >
                      <SelectTrigger className="h-7 w-[72px] text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="FS">FS</SelectItem>
                        <SelectItem value="SS">SS</SelectItem>
                        <SelectItem value="FF">FF</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      value={Number.isFinite(d.lag) ? d.lag : 0}
                      onChange={(e) => {
                        const n = parseInt(e.target.value, 10);
                        updateEntry(d.kind, d.id, { lag: Number.isFinite(n) ? n : 0 });
                      }}
                      className="h-7 w-[72px] text-xs"
                      title="Lag (ngày, có thể âm)"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => remove(d.kind, d.id)}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        )}
      </div>

      <ScrollArea className={`${scrollHeightClass} rounded-md border`}>
        <div className="p-3">
          {isEmpty && (
            <div className="py-8 text-center text-sm text-muted-foreground">
              Không có hạng mục / mốc nào.
            </div>
          )}

          {itemGroups.map(({ cat, items }) => (
            <div
              key={`item-cat-${cat}`}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-1 mb-4"
            >
              <div className="col-span-full text-xs font-semibold uppercase tracking-wide text-muted-foreground px-1 pt-1">
                {cat}
              </div>
              {items.map(it => {
                const v = `item:${it.id}`;
                const checked = valueSet.has(v);
                return (
                  <label
                    key={v}
                    className={`flex items-center gap-2 px-2 py-1.5 rounded-sm cursor-pointer hover:bg-muted ${checked ? 'bg-primary/5' : ''}`}
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => toggle('item', it.id)}
                    />
                    <span className="text-sm truncate">{it.name}</span>
                  </label>
                );
              })}
            </div>
          ))}

          {milestoneGroups.map(({ parent, items }) => (
            <div
              key={`ms-parent-${parent}`}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-1 mb-4"
            >
              <div className="col-span-full text-xs font-semibold uppercase tracking-wide text-muted-foreground px-1 pt-1">
                {parent}
              </div>
              {items.map(ms => {
                const v = `milestone:${ms.id}`;
                const checked = valueSet.has(v);
                return (
                  <label
                    key={v}
                    className={`flex items-center gap-2 px-2 py-1.5 rounded-sm cursor-pointer hover:bg-muted ${checked ? 'bg-primary/5' : ''}`}
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => toggle('milestone', ms.id)}
                    />
                    <span className="text-sm truncate">{ms.name}</span>
                  </label>
                );
              })}
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}
